import { TextFacetItem } from '@1po/1po-bff-fe-spec/generated/catalog/references/iam/response/GetIAMReferencesResponse';
import { ReferenceStock } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/model/ReferenceStock';
import { TireItem } from '@1po/1po-bff-fe-spec/generated/tire/response/TireSearchResponse';
import { TFunction } from 'i18next';
import {
  ENTRY_BRAND_LABEL,
  EXTRA_LOAD_LABEL,
  FILTER_BRAND,
  FILTER_DELIVERY,
  FILTER_FEATURES,
  FILTER_FUEL_EFFICIENCY,
  FILTER_NOISE_LEVEL,
  FILTER_PRICES,
  FILTER_PRODUCT_RANGE,
  FILTER_SEASON,
  FILTER_WET_GRIP,
  Filters,
  isFilteredStocks,
  isNotInRange,
  MEDIUM_BRAND_LABEL,
  PMSF_LABEL,
  POSITIVE_FEATURE_VALUES,
  PREMIUM_BRAND_LABEL,
  RangeFacetLocal,
  RUN_FLAT_LABEL,
  SELF_SEAL_LABEL,
  TextFacetLocal,
} from 'components/Filter/Filter.types';
import { ReferencePriceType } from 'domains/references';
import { TireBrandDetailLocal } from 'domains/tires/Tire.types';
import { GarageView, SparePartsViewType } from 'domains/user';
import { tireBrandsData } from 'pages/TiresPage/TireSearch/TiresSearchbar/TireBrandsData';
import { FOUND, getData, hasData, NO_DATA, SearchData } from 'utils';

interface FilterContext {
  filterItems: string[];
  tire: TireItem;
  stock: SearchData<Map<string, ReferenceStock>> | undefined;
  filters: Filters;
  brandCategoriesMap: SearchData<Map<string, TireBrandDetailLocal>>;
  t: TFunction;
}

interface GetFilteredTiresParams {
  availableTires: TireItem[];
  filters: Filters;
  prices: { prices: NO_DATA | ReferencePriceType; reference: string }[];
  sparePartsView: SparePartsViewType;
  showPriceless: boolean;
  stocks: Map<string, SearchData<Map<string, ReferenceStock>> | undefined>;
  brandCategoriesMap: SearchData<Map<string, TireBrandDetailLocal>>;
  t: TFunction;
}

const createDeliveryFilters = (label: string): Filters => ({
  textFilters: new Map([[FILTER_DELIVERY, [label]]]),
  rangeFilters: new Map(),
});

const isMatchingDelivery = (
  stock: SearchData<Map<string, ReferenceStock>> | undefined,
  label: string,
  t: TFunction,
): boolean => isFilteredStocks(stock, createDeliveryFilters(label), t);

const handleFeaturesFilter = (tfi: TextFacetItem, tire: TireItem): boolean => {
  switch (tfi.label) {
    case SELF_SEAL_LABEL:
      return tire.selfSeal ? POSITIVE_FEATURE_VALUES.includes(tire.selfSeal) : false;
    case PMSF_LABEL:
      return tire.pmsf ? POSITIVE_FEATURE_VALUES.includes(tire.pmsf) : false;
    case EXTRA_LOAD_LABEL:
      return tire.extraLoad ? tire.extraLoad.length > 0 : false;
    case RUN_FLAT_LABEL:
      return tire.runFlat ? tire.runFlat.length > 0 : false;
    default:
      return false;
  }
};

const handleProductRangeFilter = (
  tfi: TextFacetItem,
  tire: TireItem,
  brandCategoriesMap: SearchData<Map<string, TireBrandDetailLocal>>,
): boolean => {
  if (tire.brandId && brandCategoriesMap.searchStatus === FOUND) {
    const tireBrandDetail = brandCategoriesMap.data?.get(tire.brandId);
    switch (tfi.label) {
      case PREMIUM_BRAND_LABEL:
        return tireBrandDetail?.category === PREMIUM_BRAND_LABEL;
      case MEDIUM_BRAND_LABEL:
        return tireBrandDetail?.category === MEDIUM_BRAND_LABEL;
      case ENTRY_BRAND_LABEL:
        return tireBrandDetail?.category === ENTRY_BRAND_LABEL;
      default:
        return false;
    }
  }
  return false;
};
const getResultItems = (
  tf: TextFacetLocal,
  tfi: TextFacetItem,
  filteredTires: TireItem[],
  stocks: Map<string, SearchData<Map<string, ReferenceStock>> | undefined>,
  brandCategoriesMap: SearchData<Map<string, TireBrandDetailLocal>>,
  t: TFunction,
): TireItem[] => {
  return filteredTires.filter((tire) => {
    const stock = stocks.get(tire.partNumber);
    switch (tf.id) {
      case FILTER_BRAND: {
        const b = tireBrandsData.find((tireBrand) => tire.brandId === tireBrand.brandId);
        return b ? b.brandId === tfi.label : tfi.label === tire?.brandId;
      }
      case FILTER_SEASON:
        return tfi.label === tire?.season;
      case FILTER_DELIVERY: {
        return isMatchingDelivery(stock, tfi.label, t);
      }
      case FILTER_FUEL_EFFICIENCY:
        return tfi.label === tire?.fuelConsumption;
      case FILTER_WET_GRIP:
        return tfi.label === tire?.wetGrip;
      case FILTER_NOISE_LEVEL:
        return tfi.label === tire?.exteriorSound;
      case FILTER_FEATURES:
        return handleFeaturesFilter(tfi, tire);
      case FILTER_PRODUCT_RANGE:
        return handleProductRangeFilter(tfi, tire, brandCategoriesMap);
      default:
        return false;
    }
  });
};

export const getUpdatedTireTextFilters = (
  textFilters: TextFacetLocal[],
  stocks: Map<string, SearchData<Map<string, ReferenceStock>> | undefined>,
  filteredTires: TireItem[],
  brandCategoriesMap: SearchData<Map<string, TireBrandDetailLocal>>,
  t: TFunction,
): TextFacetLocal[] =>
  textFilters.map((textFilter) => {
    let filteredItems: TextFacetItem[];

    if (textFilter.id === FILTER_BRAND) {
      const itemsByLabel = new Map<string, number>();
      textFilter.items.forEach((item) => {
        const existing = itemsByLabel.get(item.label) ?? 0;
        itemsByLabel.set(item.label, existing + item.numberOfItems);
      });

      filteredItems = tireBrandsData
        .map((brand) => {
          const totalCount = itemsByLabel.get(brand.brandId);
          return totalCount === undefined ? undefined : { label: brand.brandId, numberOfItems: totalCount };
        })
        .filter((item): item is TextFacetItem => item !== undefined);
    } else {
      filteredItems = textFilter.items;
    }

    return {
      ...textFilter,
      items: filteredItems.map((item) => ({
        ...item,
        numberOfItems: getResultItems(textFilter, item, filteredTires, stocks, brandCategoriesMap, t).length,
      })),
    };
  });

export const getUpdatedTireRangeFilters = (
  rangeFilters: RangeFacetLocal[],
  filteredTires: TireItem[],
): RangeFacetLocal[] => {
  return rangeFilters.map((rf) => {
    if (rf.id === FILTER_PRICES) {
      return { ...rf, numberOfItems: filteredTires.length };
    } else {
      return rf;
    }
  });
};

export const getTireDeliveryFilterOptions = (
  stocksFilterOptions: TextFacetLocal,
  stocks: Map<string, SearchData<Map<string, ReferenceStock>> | undefined>,
  filteredTires: TireItem[],
  t: TFunction,
): TextFacetLocal => {
  const getItems: TextFacetItem[] = stocksFilterOptions.items.filter((soi) =>
    [...stocks.values()].some((s) => isMatchingDelivery(s, soi.label, t)),
  );
  const newItemsMap = new Map<string, TextFacetItem>();
  getItems.forEach((tfi) => {
    const resultItems = filteredTires.filter((reference) => {
      const stock = stocks.get(reference.partNumber);
      return isMatchingDelivery(stock, tfi.label, t);
    });
    newItemsMap.set(tfi.label, { label: tfi.label, numberOfItems: resultItems.length });
  });

  return {
    ...stocksFilterOptions,
    items: Array.from([...newItemsMap], ([label, value]) => ({
      numberOfItems: value.numberOfItems,
      label,
    })),
  };
};

export const refPrice = (
  sparePartsView: SparePartsViewType,
  item: { reference?: string; prices?: ReferencePriceType | NO_DATA },
): number => {
  return Number(
    sparePartsView === GarageView
      ? getData(item.prices)?.garageView?.vatExcludedPrice
      : getData(item.prices)?.clientView?.recommendedPriceVatIncluded,
  );
};

const handleFeaturesFilterArray = (filterItems: string[], tire: TireItem): boolean => {
  return filterItems.every((filter) => {
    if (filter === SELF_SEAL_LABEL) {
      return tire.selfSeal ? !POSITIVE_FEATURE_VALUES.includes(tire.selfSeal) : true;
    }
    if (filter === PMSF_LABEL) {
      return tire.pmsf ? !POSITIVE_FEATURE_VALUES.includes(tire.pmsf) : true;
    }
    if (filter === EXTRA_LOAD_LABEL) {
      return tire.extraLoad ? tire.extraLoad.length === 0 : true;
    }
    if (filter === RUN_FLAT_LABEL) {
      return tire.runFlat ? tire.runFlat.length === 0 : true;
    }
    return true;
  });
};

const isBrandFilteredOut = (filterItems: string[], tire: TireItem): boolean => {
  const currTireBrand = tireBrandsData.find((tireBrand) => tire.brandId === tireBrand.brandId);
  const currentBrandId = currTireBrand?.brandId ?? tire.brandId;
  if (!currentBrandId) {
    return true;
  }
  return !filterItems.includes(currentBrandId);
};

const isProductRangeFilteredOut = (
  filterItems: string[],
  tire: TireItem,
  brandCategoriesMap: SearchData<Map<string, TireBrandDetailLocal>>,
): boolean => {
  if (brandCategoriesMap.searchStatus === FOUND && tire.brandId) {
    const tireBrandDetail = brandCategoriesMap.data?.get(tire.brandId)?.category;
    return !filterItems.includes(tireBrandDetail ?? '');
  }
  return false;
};

const facetFilterPredicates: Record<string, (context: FilterContext) => boolean> = {
  [FILTER_BRAND]: ({ filterItems, tire }) => isBrandFilteredOut(filterItems, tire),
  [FILTER_SEASON]: ({ filterItems, tire }) => !filterItems.includes(tire?.season ?? ''),
  [FILTER_DELIVERY]: ({ stock, filters, t }) => !isFilteredStocks(stock, filters, t),
  [FILTER_FUEL_EFFICIENCY]: ({ filterItems, tire }) => !filterItems.includes(tire?.fuelConsumption ?? ''),
  [FILTER_WET_GRIP]: ({ filterItems, tire }) => !filterItems.includes(tire?.wetGrip ?? ''),
  [FILTER_NOISE_LEVEL]: ({ filterItems, tire }) => !filterItems.includes(tire?.exteriorSound ?? ''),
  [FILTER_FEATURES]: ({ filterItems, tire }) => handleFeaturesFilterArray(filterItems, tire),
  [FILTER_PRODUCT_RANGE]: ({ filterItems, tire, brandCategoriesMap }) =>
    isProductRangeFilteredOut(filterItems, tire, brandCategoriesMap),
};

const isFilterApplicableToTire = (
  filters: Filters,
  tire: TireItem,
  stocks: Map<string, SearchData<Map<string, ReferenceStock>> | undefined>,
  brandCategoriesMap: SearchData<Map<string, TireBrandDetailLocal>>,
  t: TFunction,
): boolean => {
  const stock = stocks.get(tire.partNumber);
  const isTextFiltered = [...filters.textFilters.keys()].some((facetId) => {
    const filterItems = filters.textFilters.get(facetId) ?? [];
    const predicate = facetFilterPredicates[facetId];
    if (!predicate) {
      return false;
    }
    return predicate({ filterItems, tire, stock, filters, brandCategoriesMap, t });
  });
  return !isTextFiltered;
};

export const getFilteredTires = ({
  availableTires,
  filters,
  prices,
  sparePartsView,
  showPriceless,
  stocks,
  brandCategoriesMap,
  t,
}: GetFilteredTiresParams): TireItem[] => {
  const textFilteredReferences = availableTires.filter((tire) =>
    isFilterApplicableToTire(filters, tire, stocks, brandCategoriesMap, t),
  );
  const priceFilterRange = filters.rangeFilters.get(FILTER_PRICES);
  const pricesInRange = prices.filter((price) => {
    if (!priceFilterRange) {
      return true;
    }
    return !(
      isNotInRange([String(refPrice(sparePartsView, price))], {
        min: priceFilterRange.min,
        max: priceFilterRange.max,
      }) ||
      (!hasData(price) && showPriceless)
    );
  });
  const priceReferencesInRange = new Set(pricesInRange.map((priceInRange) => priceInRange.reference));

  return textFilteredReferences.filter((tire) => priceReferencesInRange.has(tire.partNumber));
};
